import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { createSequence, editSequenceSteps } from '@/services/sequencesService';
import { sendAnalytics } from '@/services/analyticsService';
import type { RootState } from '@/rootReducer';
import Tooltip from '@/components/Tooltip/Tooltip';
import Button from '@/components/Button/newButtons';
import StepNumber from '@/components/StepNumber/StepNumber';
import Loader, { Status } from '@/components/Loader/Loader';
import { normalizeHTML } from '@/utils/normalizeHTML';
import { useIsScreenWidthSmallerThan } from '@/hooks/useIsScreenWidthSmallerThan';
import { isEqual } from 'lodash';
import { useSelectBusinessMutation } from '@/modules/lists/details/src/hooks/useSelectBusinessMutation';
import { useToast } from '@/components/atoms/Toast/hooks/useToast';
import { useSequencesContext } from '../SequencesContext';
import { StepsType, useSequences } from '../hooks/useSequences';
import { useSendAllSequence } from '../hooks/useSendAllSequences';
import { useOnboardingScore } from '../hooks/useOnboardingScore';
import CalendlyModal from './CalendlyModal';

export default function NewSequenceFooter() {
	const { toast } = useToast();
	const {
		selectedStep,
		sequencePage,
		setSequencePage,
		selectedTemplate,
		setShowSendAll,
		selectedBusinesses,
		selectedSequence,
		setSelectedSequence,
		aiSequenceModalFields,
		setAiSequenceModalFields,
		sequenceEnroll,
		selectedLists
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserOnboarding = user?.onboarding?.status === 'pending';

	const { fetchSequences } = useSequences();

	const [draftStatus, setDraftStatus] = useState<'IDLE' | 'SAVING' | 'SAVED'>(
		'IDLE'
	);

	const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const prevSelectedStepRef = useRef<StepsType | null>(null);
	const hasInitializedRef = useRef(false);
	const screenSize = useIsScreenWidthSmallerThan(1024);

	const handleSkipButton = () => {
		setSequencePage('MY-SEQUENCES');
	};

	const [autoSaveStatus, setAutoSaveStatus] = useState<Status>(Status.NONE);

	const handleGoToStepsCreation = () => {
		if (!selectedSequence || !selectedTemplate) return;

		if (!selectedTemplate.isAiGenerated) {
			setAiSequenceModalFields(undefined);
		}

		setSequencePage('STEPS-CREATION');
		setSelectedSequence({
			...selectedSequence,
			steps: selectedTemplate.steps
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			templateId: selectedTemplate?.id,
			eventName: 'user_selected_a_sequence_template'
		});
	};

	const handleSequenceAction = useCallback(
		(action: 'autoSave' | 'saveDraft' | 'nextStep') => {
			if (!selectedSequence) return;

			// If the sequence doesnt have an id that means it is a new sequence, so it needs to be created
			const isNewSequence = !selectedSequence.id;

			if (action === 'saveDraft') {
				setDraftStatus('SAVING');
			}

			// If the campaign got saved automatically, don't trigger the endpoint when hitting next
			if (action === 'nextStep' && draftStatus === 'SAVED') {
				fetchSequences();
				setSequencePage('ADD-BUSINESS');

				return;
			}

			const handleSuccess = (message: string) => {
				setDraftStatus('SAVED');

				if (action === 'autoSave') {
					setAutoSaveStatus(Status.SUCCESS);
				}

				if (action === 'saveDraft') {
					toast({
						title: message,
						variant: 'success'
					});

					setTimeout(() => {
						fetchSequences();
						setSequencePage('MY-SEQUENCES');
					}, 1000);
				}

				if (action === 'nextStep') {
					toast({
						title: message,
						variant: 'success'
					});

					setTimeout(() => {
						fetchSequences();
						setSequencePage('ADD-BUSINESS');
					}, 1000);
				}

				setTimeout(() => {
					setAutoSaveStatus(Status.NONE);
				}, 3000);
			};

			const handleError = (error: Error) => {
				if (action === 'autoSave') {
					setAutoSaveStatus(Status.FAILURE);
				}

				if (action === 'nextStep' || action === 'saveDraft') {
					toast({
						title: error.message,
						variant: 'error'
					});
				}

				setDraftStatus('IDLE');
			};

			if (isNewSequence) {
				createSequence({
					name: selectedSequence.name,
					steps: selectedSequence.steps.map(step => ({
						...step,
						body: normalizeHTML(step.body)
					})),
					status: 'DRAFT',
					creationSource: aiSequenceModalFields ? 'AI' : 'User'
				})
					.then(res => {
						if (res.status !== 200) {
							toast({
								title: 'Error saving draft! Please try again later.',
								variant: 'error'
							});
						}

						setSelectedSequence({
							...selectedSequence,
							id: res.data?.data.id,
							justCreated: true
						});

						handleSuccess('Draft saved successfully!');
					})
					.catch(handleError);
			} else {
				// Generate steps to update based on their ID format
				const stepsToUpdate = selectedSequence.steps.map(step => {
					// Check if this step has an ID and if it's not in UUID format
					// The backend expects valid UUIDs, but frontend may generate temporary IDs
					if (
						step.id &&
						!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
							step.id
						)
					) {
						// For steps with temporary IDs, create a new object without the ID property
						return {
							body: normalizeHTML(step.body),
							subject: step.subject,
							sendAfterInterval: step.sendAfterInterval,
							sendAfterType: step.sendAfterType,
							order: step.order
						};
					}
					// For steps with valid UUID IDs or no ID, keep them unchanged
					return {
						...step,
						body: normalizeHTML(step.body)
					};
				});

				// If the sequence already exists, update it
				editSequenceSteps({
					campaignId: selectedSequence.id ?? '',
					steps: stepsToUpdate,
					...(selectedSequence.creationSource === 'AI' && {
						manuallyEdited: true
					})
				})
					.then(res => {
						if (res.status !== 200) {
							toast({
								title: 'Error saving sequence! Please try again later.',
								variant: 'error'
							});
						}

						handleSuccess('Draft saved successfully!');
					})
					.catch(handleError);
			}

			if (action !== 'autoSave') {
				if (action === 'nextStep') {
					sendAnalytics({
						type: 'track',
						userId: user.email,
						eventName:
							'Onboarding Flow - Clicked Next on Steps Creation Screen',
						properties: {
							isUserOnboarding
						}
					});
					return;
				}
				sendAnalytics({
					type: 'track',
					userId: user.email,
					sequenceId: selectedSequence.id,
					properties: {
						isUserOnboarding
					},
					eventName: (() => {
						if (action === 'saveDraft') {
							return 'user_saved_sequence_as_draft';
						}

						if (action === 'nextStep') {
							return 'User proceeded to add business page in sequences';
						}

						return '';
					})()
				});
			}
		},
		[
			selectedSequence,
			draftStatus,
			fetchSequences,
			setSequencePage,
			toast,
			aiSequenceModalFields,
			setSelectedSequence,
			user.email,
			toast
		]
	);

	const handleOpenSendAll = () => {
		setShowSendAll(true);
		if (isUserOnboarding) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Onboarding Flow - Clicked Review',
				properties: {
					isUserOnboarding
				}
			});
		}
	};

	const stripHtml = (html: string) => {
		const tmp = document.createElement('DIV');
		tmp.innerHTML = html;
		return tmp.textContent || tmp.innerText || '';
	};

	const isReviewButtonDisabled =
		selectedBusinesses.length === 0 || selectedLists.length === 0;

	const [isSequenceInvalid, errorMessage] = useMemo(() => {
		const steps = selectedSequence?.steps || [];

		// A Sequence must have at least two steps
		const hasAtLeastTwoSteps = steps.length >= 2;
		// Each step must have a subject and body
		const hasValidSteps = steps.every(
			step => step.subject && stripHtml(step.body).trim() !== ''
		);
		// AI Snippet can only be placed in the first step
		const hasAISnippetAfterFirstStep = steps.some(
			step => step.body.includes('[ai_snippet]') && step.order > 0
		);
		// The body of each step must not contain duplicate AI Snippets
		const hasDuplicateAISnippet = steps.some(
			step => (step.body.match(/\[ai_snippet\]/g) || []).length > 1
		);
		// The body must not contain brackets information other than [business_name] or [ai_snippet]
		const invalidBracket = steps
			.map(step => {
				const bodyAndSubject = `${step.body} ${step.subject}`;
				const matches = bodyAndSubject.match(/\[.*?\]/g) || [];
				const invalidBrackets = matches.filter(
					match =>
						match !== '[business_name]' &&
						match !== '[ai_snippet]' &&
						match !== '[list_name]'
				);
				return invalidBrackets.length > 0 ? invalidBrackets[0] : null;
			})
			.find(match => match);
		// The body and suject must not contain braces
		const invalidBrace = steps
			.map(step => {
				const bodyAndSubject = `${step.body} ${step.subject}`;
				const matches = bodyAndSubject.match(/\{.*?\}/g) || [];
				return matches.length > 0 ? matches[0] : null;
			})
			.find(match => match);

		const error =
			(!hasAtLeastTwoSteps &&
				'Your sequence must have at least two steps.') ||
			(!hasValidSteps && 'Each step must have a subject and body.') ||
			(hasAISnippetAfterFirstStep &&
				'AI Snippet can only be placed in the first step.') ||
			(hasDuplicateAISnippet &&
				'The body must contain only one AI Snippet.') ||
			(invalidBrace &&
				`Replace text in braces with the appropriate info: ${invalidBrace}`) ||
			(invalidBracket &&
				`You have a invalid custom text: ${invalidBracket}`);

		setDraftStatus('IDLE');

		return [
			!hasAtLeastTwoSteps ||
				!hasValidSteps ||
				hasAISnippetAfterFirstStep ||
				hasDuplicateAISnippet ||
				invalidBrace !== undefined ||
				invalidBracket !== undefined,
			error
		];
	}, [selectedSequence]);

	useEffect(() => {
		const currentStep = selectedStep;

		if (!currentStep) return;

		// First run: skip comparison, save for the first time
		const shouldRun =
			!hasInitializedRef.current ||
			!isEqual(prevSelectedStepRef.current, currentStep);

		if (!shouldRun) return;

		// Update previous step reference
		prevSelectedStepRef.current = currentStep;

		// Clear existing timeout
		if (autoSaveTimeoutRef.current) {
			clearTimeout(autoSaveTimeoutRef.current);
		}

		// Set new timeout
		autoSaveTimeoutRef.current = setTimeout(() => {
			if (!isSequenceInvalid) {
				hasInitializedRef.current = true;
				setAutoSaveStatus(Status.LOADING);
				handleSequenceAction('autoSave');
			}
		}, 5000);

		return () => {
			if (autoSaveTimeoutRef.current) {
				clearTimeout(autoSaveTimeoutRef.current);
			}
		};
	}, [selectedStep, handleSequenceAction, isSequenceInvalid]);

	const { score, loading: scoreLoading } = useOnboardingScore();
	const [showCalendly, setShowCalendly] = useState(false);

	const { triggerSendAll } = useSendAllSequence();

	const handleSetSequenceLiveClick = () => {
		if (scoreLoading) return;

		if (score && score >= 3) {
			setShowCalendly(true);
			return;
		}

		triggerSendAll();
	};

	const handleCalendlyClose = () => {
		setShowCalendly(false);
	};

	return (
		<div className="flex min-h-16 flex-shrink-0 flex-col-reverse items-end justify-between gap-y-5 rounded-xl bg-dm-black p-2 pt-4 lg:flex-row lg:items-center lg:gap-y-3 lg:rounded-none lg:rounded-tl-lg lg:px-8 lg:pt-2">
			<SequenceProgressSteps
				isAddBusinessDisabled={isSequenceInvalid}
				isStepsCreationDisabled={selectedTemplate === undefined}
			/>
			{sequencePage === 'SEQUENCE-START' && !screenSize.isSmaller && (
				<Button
					onClick={handleGoToStepsCreation}
					disabled={selectedTemplate === undefined}
					className="px-6 py-2"
				>
					Select and continue
				</Button>
			)}
			{sequencePage === 'STEPS-CREATION' && (
				<div className="relative flex items-center gap-5">
					{errorMessage && (
						<div className="absolute -left-8">
							<Tooltip
								content={errorMessage || ''}
								longerBubble
								size="md"
								position="top"
							>
								<div className="flex h-5 w-5 items-center justify-center rounded-full border border-yellow-600 text-yellow-600">
									!
								</div>
							</Tooltip>
						</div>
					)}

					{!isSequenceInvalid &&
						autoSaveStatus !== Status.NONE &&
						!isUserOnboarding && (
							<div className="inline-flex items-center text-base">
								<Loader
									className="emailModal-auto-save--loader mr-1.5"
									status={autoSaveStatus}
								/>
								{autoSaveStatus === Status.LOADING && (
									<span>Saving...</span>
								)}
								{autoSaveStatus === Status.SUCCESS && (
									<span>Auto-saved</span>
								)}
								{autoSaveStatus === Status.FAILURE && (
									<span>Error auto saving</span>
								)}
							</div>
						)}

					{!isUserOnboarding && (
						<Button
							disabled={isSequenceInvalid}
							onClick={() => handleSequenceAction('saveDraft')}
							variant="secondary"
						>
							{(draftStatus === 'IDLE' ||
								draftStatus === 'SAVED') &&
								'Save Draft'}
							{draftStatus === 'SAVING' && 'Saving...'}
						</Button>
					)}

					{/* If the Sequence is being edited it should no have ne next button */}
					{(sequenceEnroll ||
						selectedSequence?.status === 'DRAFT') && (
						<Button
							disabled={isSequenceInvalid}
							onClick={() => handleSequenceAction('nextStep')}
						>
							Next
						</Button>
					)}
				</div>
			)}
			{sequencePage === 'ADD-BUSINESS' && (
				<div className="flex gap-5">
					{!isUserOnboarding ? (
						<>
							<Button
								variant="secondary"
								onClick={handleSkipButton}
								disabled={selectedTemplate === undefined}
							>
								Skip
							</Button>
							<Button
								disabled={isReviewButtonDisabled}
								onClick={handleOpenSendAll}
							>
								Review
							</Button>
						</>
					) : (
						<Button
							onClick={handleSetSequenceLiveClick}
							disabled={scoreLoading || isReviewButtonDisabled}
						>
							Set Sequence Live
						</Button>
					)}
					{showCalendly && (
						<CalendlyModal
							calendlyUrl="https://calendly.com/d/cqdm-gr6-4yr?utm_source=trial"
							onClose={handleCalendlyClose}
						/>
					)}
				</div>
			)}
		</div>
	);
}

export function SequenceProgressSteps({
	isAddBusinessDisabled,
	isStepsCreationDisabled
}: {
	isAddBusinessDisabled: boolean;
	isStepsCreationDisabled: boolean;
}) {
	const { sequencePage, setSequencePage, selectedSequence, sequenceEnroll } =
		useSequencesContext();

	const { mutate: mutateSelectBusiness } = useSelectBusinessMutation();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserOnboarding = user?.onboarding?.status === 'pending';

	const pagesOptions = {
		'MY-SEQUENCES': 0,
		'SEQUENCE-START': 1,
		'STEPS-CREATION': 2,
		'ADD-BUSINESS': 3
	};

	const currentNumber = pagesOptions[sequencePage];
	const selectedSequenceExists = selectedSequence?.id !== undefined;
	const shouldHideAddBusiness =
		!sequenceEnroll &&
		selectedSequenceExists &&
		!selectedSequence.justCreated &&
		selectedSequence?.status !== 'DRAFT';

	const handleStepClick = (page: keyof typeof pagesOptions) => {
		setSequencePage(page);
		mutateSelectBusiness(null);
		if (isUserOnboarding) {
			let eventName = '';

			if (page === 'STEPS-CREATION') {
				eventName = 'Onboarding Flow - Clicked Steps Creation';
			} else if (page === 'ADD-BUSINESS') {
				eventName = 'Onboarding Flow - Clicked Add Business';
			}

			if (eventName) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName,
					properties: {
						step: page,
						isUserOnboarding: true
					}
				});
			}
		} else {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User clicked step navigation',
				properties: {
					step: page,
					isUserOnboarding
				}
			});
		}
	};

	return (
		<div
			data-should-hide-add-business={shouldHideAddBusiness}
			className="flex min-h-10 w-full select-none overflow-hidden rounded-lg border border-dm-grey text-center data-[should-hide-add-business=false]:max-w-4xl data-[should-hide-add-business=true]:max-w-2xl lg:w-[43.75rem] lg:rounded-2xl"
		>
			<button
				type="button"
				disabled={isUserOnboarding}
				onClick={() => handleStepClick('SEQUENCE-START')}
				className="group relative m-auto flex h-full w-full cursor-pointer flex-col items-center justify-center gap-x-10 gap-y-1 py-2 disabled:cursor-not-allowed lg:flex-row lg:gap-3 lg:py-0"
			>
				<StepNumber
					number={1}
					currentNumber={currentNumber}
					stepText="Sequence start"
					disabled={isUserOnboarding}
				/>
				<div
					data-current-number={currentNumber >= 1}
					className="absolute bottom-0 right-0 top-0 my-auto hidden h-9 w-9 border-r border-t group-disabled:border-dm-grey data-[current-number=true]:border-orange lg:block"
					style={{
						rotate: '45deg'
					}}
				/>
			</button>

			<button
				type="button"
				onClick={() => handleStepClick('STEPS-CREATION')}
				disabled={isStepsCreationDisabled}
				className="relative m-auto flex h-full w-full cursor-pointer flex-col items-center justify-center gap-x-10 gap-y-1 py-2 lg:flex-row lg:gap-3 lg:py-0"
			>
				<StepNumber
					number={2}
					currentNumber={currentNumber}
					stepText="Steps creation"
				/>
				<div
					data-current-number={currentNumber >= 2}
					data-should-hide-add-business={shouldHideAddBusiness}
					className="absolute bottom-0 right-0 top-0 my-auto hidden h-9 w-9 data-[should-hide-add-business=false]:border-r data-[should-hide-add-business=false]:border-t data-[current-number=true]:border-orange lg:block"
					style={{
						rotate: '45deg'
					}}
				/>
			</button>

			{!shouldHideAddBusiness && (
				<button
					type="button"
					onClick={() => handleStepClick('ADD-BUSINESS')}
					disabled={isAddBusinessDisabled}
					className="m-auto flex h-full w-full cursor-pointer flex-col items-center justify-center gap-x-10 gap-y-1 py-2 lg:flex-row lg:gap-3 lg:py-0"
				>
					<StepNumber
						number={3}
						currentNumber={currentNumber}
						stepText="Add businesses"
					/>
				</button>
			)}
		</div>
	);
}
